import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, takeWhile } from 'rxjs/operators';
import { RequestState } from '../../../shared/definitions/request-state.enum';
import { SimXReleaseTag } from '../../../shared/models/entities/simx-release-tag.model';
import { ReleaseTagDataService } from '../../../shared/services/release-tag-data.service';
import * as ReleaseTagStoreActions from '../store/release-tag-store.actions';
import * as ReleaseTagStoreSelectors from '../store/release-tag-store.selectors';

@Injectable({ providedIn: 'root' })
export class ReleaseTagService {
  private readonly _releaseTagDataService = inject(ReleaseTagDataService);
  private readonly _store = inject(Store);

  requestFetchReleaseTags(): void {
    this._store.dispatch(ReleaseTagStoreActions.requestReleaseTags());
  }

  fetchAllReleaseTags() {
    return this._releaseTagDataService.getReleaseTags().pipe(
      map(releaseTags =>
        ReleaseTagStoreActions.fetchReleaseTagsSucceeded({ releaseTags }),
      ),
      catchError(() => of(ReleaseTagStoreActions.fetchReleaseTagsFailed())),
    );
  }

  getReleaseTags(): Observable<Array<SimXReleaseTag>> {
    return new Observable<Array<SimXReleaseTag>>(observer => {
      this.requestFetchReleaseTags();
      this._store
        .select(ReleaseTagStoreSelectors.selectReleaseTagsRequestState)
        .pipe(
          takeWhile(
            requestState =>
              [RequestState.Initial, RequestState.InProgress].includes(
                requestState,
              ),
            true,
          ),
          filter(
            requestState =>
              ![RequestState.Initial, RequestState.InProgress].includes(
                requestState,
              ),
          ),
          switchMap(() =>
            this._store.select(ReleaseTagStoreSelectors.selectReleaseTags),
          ),
        )
        .subscribe(releaseTags => {
          observer.next(releaseTags);
          observer.complete();
        });
    });
  }
}
